import { formatCategoryLabel } from './labels';

const money = value => `$${Math.round(Number(value) || 0).toLocaleString()}`;

const sectionKeys = [
  ['operations', 'Operations'],
  ['customer', 'Customer Experience'],
  ['marketing', 'Sales & Marketing'],
  ['team', 'Team & Delegation']
];

const classifyQuadrant = ({ impact = 0, difficulty = 3 }) => {
  if (impact > 3 && difficulty <= 3) return 'quickWins';
  if (impact > 3 && difficulty > 3) return 'strategic';
  if (impact <= 3 && difficulty <= 3) return 'fillIns';
  return 'timeSinks';
};

const quadrantLabels = {
  quickWins: 'Quick Wins',
  strategic: 'Strategic Bets',
  fillIns: 'Useful Fill-ins',
  timeSinks: 'Defer or Redesign'
};

const buildFindings = (diagnostic = {}, riskInputs = {}) => {
  const riskFlags = diagnostic.risk_flags || [];
  const dataGaps = diagnostic.data_readiness_gaps || [];
  const findings = [];

  riskFlags.slice(0, 2).forEach(flag => {
    findings.push({
      title: flag,
      type: 'Responsible AI risk',
      impact: 'May create privacy, accuracy, or accountability risk if AI is adopted too quickly.',
      fix: 'Write the rule first, assign a human reviewer, and pilot with low-risk examples.',
      effort: '30-90 minutes',
      confidence: 'Based on diagnostic risk signals'
    });
  });

  dataGaps.slice(0, 2).forEach(gap => {
    findings.push({
      title: gap,
      type: 'Data readiness gap',
      impact: 'Weak source data can make AI outputs inconsistent and hard to trust.',
      fix: 'Clean the source field or checklist before asking AI to automate the workflow.',
      effort: '1-3 hours',
      confidence: 'Based on readiness answers'
    });
  });

  if (!riskInputs.approval_owner) {
    findings.push({
      title: 'No clear AI approval owner was captured',
      type: 'Governance gap',
      impact: 'Teams may adopt tools faster than the business can supervise them.',
      fix: 'Name one person who approves AI pilots and reviews exceptions.',
      effort: '15 minutes',
      confidence: 'Based on missing owner input'
    });
  }

  if (findings.length === 0) {
    findings.push({
      title: 'Start with written AI usage boundaries',
      type: 'Stewardship foundation',
      impact: 'Clear boundaries help the team use AI wisely without over-automating sensitive work.',
      fix: 'Create a one-page rule for approved tools, sensitive data, and required human review.',
      effort: '30-60 minutes',
      confidence: 'General responsible AI baseline'
    });
  }

  return findings.slice(0, 4);
};

export const getReportDashboardMetrics = (auditData = {}, report = {}, overallScore = 0) => {
  const diagnostic = report.responsible_ai_diagnostic || {};
  const riskInputs = auditData.risk_inputs || {};
  const roiItems = auditData.roi_items || [];
  const matrixItems = auditData.matrix_items || [];
  const riskKeys = ['sensitive_data', 'customer_messages', 'money_or_commitments'];
  const riskCount = riskKeys.filter(key => Boolean(riskInputs[key])).length;
  const hasRiskInput = riskKeys.some(key => Object.prototype.hasOwnProperty.call(riskInputs, key));
  const hasApprovalOwner = Boolean((riskInputs.approval_owner || '').trim());
  const readinessCount = Object.values(auditData.readiness_inputs || {}).filter(Boolean).length;
  const quickWinCount = (report.quick_wins || []).length;
  const manualWorkCost = roiItems.reduce((sum, item) => sum + (Number(item?.annual) || 0), 0);
  const valueRange = diagnostic.recoverable_value_range || {};
  const low = Number(valueRange.low) || 0;
  const high = Number(valueRange.high) || 0;

  const sectionScores = sectionKeys.map(([key, label]) => ({
    key,
    label,
    score: Number(auditData.sections?.[key]?.total) || 0
  }));

  const quadrants = {
    quickWins: { label: quadrantLabels.quickWins, count: 0, value: 0 },
    strategic: { label: quadrantLabels.strategic, count: 0, value: 0 },
    fillIns: { label: quadrantLabels.fillIns, count: 0, value: 0 },
    timeSinks: { label: quadrantLabels.timeSinks, count: 0, value: 0 }
  };

  roiItems.forEach((item, idx) => {
    const matrix = matrixItems.find(entry => Number(entry.roi_index) === idx) || {};
    const key = classifyQuadrant({ impact: Number(matrix.impact) || 0, difficulty: Number(item.difficulty) || 3 });
    quadrants[key].count += 1;
    quadrants[key].value += Number(item.annual) || 0;
  });

  const pilotConfidenceRaw = 35 + (quickWinCount > 0 ? 15 : 0) + (low > 0 ? 15 : 0) + (hasRiskInput && riskCount <= 1 ? 10 : 0) + (hasApprovalOwner ? 10 : 0) + Math.min(15, readinessCount * 5);
  const riskLevel = riskCount >= 3 ? 'High' : riskCount >= 1 ? 'Moderate' : 'Low';

  const firstPilot = diagnostic.first_pilot || {};

  return {
    stewardshipScore: Number(overallScore) || 0,
    manualWorkCost,
    sectionScores,
    quadrants,
    recoverableRange: {
      low,
      high,
      label: `${money(low)}–${money(high)}`
    },
    riskExposure: {
      level: riskLevel,
      count: riskCount,
      hasApprovalOwner,
      label: riskLevel === 'High' ? 'Controls needed before automation' : riskLevel === 'Moderate' ? 'Pilot with safeguards' : 'Low current risk signals'
    },
    pilotConfidence: {
      score: Math.min(95, pilotConfidenceRaw),
      label: pilotConfidenceRaw >= 75 ? 'Strong candidate' : pilotConfidenceRaw >= 55 ? 'Promising with guardrails' : 'Build foundation first'
    },
    firstPilot: {
      name: firstPilot.name || 'Responsible AI readiness foundation',
      why: firstPilot.why_this_first || 'Start by setting the rules, ownership, and review habits that make later AI pilots safer.',
      shape: firstPilot.pilot_shape || 'Policy and supervision foundation'
    },
    findings: buildFindings(diagnostic, riskInputs),
    topCategory: roiItems[0]?.category ? formatCategoryLabel(roiItems[0].category) : 'Operations'
  };
};
